// src/components/ImportaExcelModal.js
// Flusso di importazione Excel riusabile — carica file, mappa le colonne (con tentativo
// automatico di indovinare dai nomi), anteprima, conferma. Stesso schema già collaudato
// per il Computo Metrico / SAL, qui generalizzato per qualsiasi tipo di dato (clienti,
// dipendenti, e in futuro altro).
import React, { useState } from "react";
import { Upload, X } from "lucide-react";
import * as XLSX from "xlsx";
import { messaggioErrore } from "../utils/erroriUtente";

// campiMappabili: [{ key:"ragioneSociale", label:"Ragione Sociale", indovina:/ragione|denomina/, tipo:"date"? }, ...]
// campoObbligatorio: key del campo che deve essere per forza mappato (es. "ragioneSociale")
// onConferma: async (righeMappate) => {...}  righeMappate = [{ragioneSociale:"...", piva:"...", ...}, ...]
//
// Le date sono un caso a parte: un vero file Excel spesso ha le date come celle-data reali,
// non testo — senza cellDates:true arriverebbero come numeri seriali senza senso (es. "32950"
// invece di una data). I campi con tipo:"date" vengono normalizzati sempre in "AAAA-MM-GG"
// (formato usato ovunque in Nexiora), da Date object, numero seriale o testo gg/mm/aaaa.
function normalizzaDataExcel(valore) {
  if (valore === "" || valore === null || valore === undefined) return "";
  if (valore instanceof Date && !isNaN(valore)) {
    const y=valore.getFullYear(), m=String(valore.getMonth()+1).padStart(2,"0"), d=String(valore.getDate()).padStart(2,"0");
    return `${y}-${m}-${d}`;
  }
  const testo = String(valore).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(testo)) return testo; // già nel formato giusto
  const matchIt = testo.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/); // gg/mm/aaaa
  if (matchIt) {
    const [,g,m,a] = matchIt;
    return `${a}-${m.padStart(2,"0")}-${g.padStart(2,"0")}`;
  }
  const numerico = Number(testo);
  if (!isNaN(numerico) && numerico > 20000 && numerico < 60000) { // plausibile seriale Excel
    const epoch = new Date(Date.UTC(1899,11,30));
    const d = new Date(epoch.getTime() + numerico*86400000);
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}-${String(d.getUTCDate()).padStart(2,"0")}`;
  }
  return ""; // non riconosciuta — meglio vuota che sbagliata
}

export default function ImportaExcelModal({ label, campiMappabili, campoObbligatorio, campiExtra, onConferma, avvisoIniziale, avvisoFinale, coloreBottone }) {
  const [aperto, setAperto] = useState(false);
  const [passo, setPasso] = useState(1);
  const [righeGrezze, setRigheGrezze] = useState([]);
  const [mappaColonne, setMappaColonne] = useState({});
  const [valoriExtra, setValoriExtra] = useState(() => Object.fromEntries((campiExtra||[]).map(c=>[c.key, c.default||""])));
  const [importando, setImportando] = useState(false);

  const chiudi = () => { setAperto(false); setPasso(1); setRigheGrezze([]); setMappaColonne({}); };

  const leggiFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const wb = XLSX.read(e.target.result, { type:"array", cellDates:true });
        const primoFoglio = wb.Sheets[wb.SheetNames[0]];
        const aoa = XLSX.utils.sheet_to_json(primoFoglio, { header:1, defval:"" });
        if (aoa.length < 2) { alert("Il file sembra vuoto — deve avere almeno un'intestazione e una riga di dati"); return; }
        setRigheGrezze(aoa);
        const intestazioni = aoa[0].map(h=>String(h||"").toLowerCase());
        const indovina = {};
        intestazioni.forEach((h,i) => {
          const campo = campiMappabili.find(c => c.indovina && c.indovina.test(h));
          if (campo) indovina[i] = campo.key;
        });
        setMappaColonne(indovina);
        setPasso(2);
      } catch (err) {
        console.error("Errore lettura file importazione:", err);
        alert("Impossibile leggere il file — assicurati che sia un vero file Excel (.xlsx)");
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const conferma = async () => {
    const idxObbligatorio = Object.entries(mappaColonne).find(([,v])=>v===campoObbligatorio)?.[0];
    const campoObbligatorioLabel = campiMappabili.find(c=>c.key===campoObbligatorio)?.label || campoObbligatorio;
    if (idxObbligatorio===undefined) { alert(`Devi almeno indicare quale colonna è "${campoObbligatorioLabel}"`); return; }
    const extraMancante = (campiExtra||[]).find(c=>c.obbligatorio && !valoriExtra[c.key]);
    if (extraMancante) { alert(`Devi scegliere "${extraMancante.label}"`); return; }
    setImportando(true);
    try {
      const indici = {};
      campiMappabili.forEach(c => { indici[c.key] = Object.entries(mappaColonne).find(([,v])=>v===c.key)?.[0]; });

      const righeMappate = righeGrezze.slice(1)
        .filter(r => String(r[idxObbligatorio]||"").trim())
        .map(r => {
          const obj = {};
          campiMappabili.forEach(c => {
            if (indici[c.key]===undefined) return;
            const grezzo = r[indici[c.key]];
            obj[c.key] = c.tipo==="date" ? normalizzaDataExcel(grezzo) : String(grezzo??"").trim();
          });
          return obj;
        });

      if (righeMappate.length===0) { alert("Nessuna riga valida trovata da importare"); setImportando(false); return; }
      await onConferma(righeMappate, valoriExtra);
      chiudi();
    } catch (err) {
      console.error("Errore importazione:", err);
      alert(messaggioErrore(err, "completare l'importazione"));
    } finally {
      setImportando(false);
    }
  };

  return (
    <>
      <button onClick={()=>setAperto(true)} style={{display:"flex",alignItems:"center",gap:5,padding:"6px 12px",border:`1px solid ${coloreBottone||"#0369a1"}`,borderRadius:7,background:"#eff6ff",color:coloreBottone||"#0369a1",fontSize:12,fontWeight:700,cursor:"pointer"}}>
        <Upload size={13}/> {label||"Importa da Excel"}
      </button>

      {aperto && (
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,.4)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center"}} onClick={chiudi}>
          <div style={{background:"#fff",borderRadius:14,padding:24,width:600,maxHeight:"80vh",overflowY:"auto"}} onClick={e=>e.stopPropagation()}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
              <h3 style={{fontSize:15,fontWeight:800,color:"#1e1b4b",margin:0}}>📥 {label||"Importa da Excel"}</h3>
              <button onClick={chiudi} style={{background:"none",border:"none",cursor:"pointer",color:"#9ca3af"}}><X size={18}/></button>
            </div>

            {passo===1 && (
              <div>
                <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:11.5,color:"#5b21b6"}}>
                  💜 {avvisoIniziale||"Funziona bene se il tuo file ha una riga per ogni voce, con i dati in colonne (anche in ordine diverso dal solito, sei tu a dirci quale colonna è cosa al passo successivo)."}
                </div>
                <label style={{display:"flex",flexDirection:"column",alignItems:"center",gap:8,padding:"30px 20px",border:"2px dashed #d1d5db",borderRadius:10,cursor:"pointer",background:"#fafafa"}}>
                  <Upload size={28} color="#9ca3af"/>
                  <span style={{fontSize:13,fontWeight:600,color:"#374151"}}>Clicca per scegliere un file Excel (.xlsx)</span>
                  <input type="file" accept=".xlsx,.xls" style={{display:"none"}} onChange={e=>{ if(e.target.files[0]) leggiFile(e.target.files[0]); }}/>
                </label>
              </div>
            )}

            {passo===2 && (
              <div>
                <p style={{fontSize:12,color:"#6b7280",marginBottom:14}}>Trovate {righeGrezze[0]?.length||0} colonne, {righeGrezze.length-1} righe di dati. Dicci a cosa corrisponde ogni colonna del tuo file:</p>
                <div style={{maxHeight:280,overflowY:"auto",marginBottom:16}}>
                  {righeGrezze[0]?.map((intestazione,i) => (
                    <div key={i} style={{display:"flex",alignItems:"center",gap:10,padding:"7px 0",borderBottom:"1px solid #f3f4f6"}}>
                      <div style={{flex:1,fontSize:12,fontWeight:600,color:"#111827",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>
                        {String(intestazione||`Colonna ${i+1}`)}
                        <div style={{fontSize:10,color:"#9ca3af",fontWeight:400}}>{righeGrezze[1]?.[i] instanceof Date ? righeGrezze[1][i].toLocaleDateString("it-IT") : String(righeGrezze[1]?.[i]||"")}</div>
                      </div>
                      <select value={mappaColonne[i]||""} onChange={e=>setMappaColonne(m=>({...m,[i]:e.target.value}))}
                        style={{padding:"5px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,minWidth:200}}>
                        <option value="">— Ignora —</option>
                        {campiMappabili.map(c=><option key={c.key} value={c.key}>{c.label}</option>)}
                      </select>
                    </div>
                  ))}
                </div>
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setPasso(1)} style={{padding:"7px 14px",border:"1px solid #e5e7eb",borderRadius:7,background:"#fff",fontSize:12,cursor:"pointer"}}>← Indietro</button>
                  <button onClick={()=>setPasso(3)} className="btn btn-primary" style={{padding:"7px 14px",fontSize:12}}>Continua →</button>
                </div>
              </div>
            )}

            {passo===3 && (
              <div>
                <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:8,padding:"10px 14px",marginBottom:16,fontSize:11.5,color:"#5b21b6"}}>
                  💜 {avvisoFinale||"Arrivano i valori dal tuo file, non eventuali formule — questi dati si aggiungono a quelli che hai già, senza toccarli."}
                </div>
                <p style={{fontSize:12,color:"#374151",marginBottom:10,fontWeight:600}}>{righeGrezze.length-1} riga/e pronte per l'importazione.</p>
                {(campiExtra||[]).length>0 && (
                  <div style={{background:"#fffbeb",border:"1px solid #fde68a",borderRadius:8,padding:"12px 14px",marginBottom:16}}>
                    <div style={{fontSize:11.5,fontWeight:700,color:"#92400e",marginBottom:8}}>Questi dati non arrivano dal file — scegli qui, valgono per tutte le righe importate:</div>
                    {campiExtra.map(c => (
                      <div key={c.key} style={{marginBottom:8}}>
                        <label style={{fontSize:11,fontWeight:600,color:"#92400e",display:"block",marginBottom:3}}>{c.label}{c.obbligatorio?" *":""}</label>
                        <select value={valoriExtra[c.key]||""} onChange={e=>setValoriExtra(v=>({...v,[c.key]:e.target.value}))}
                          style={{padding:"5px 8px",border:"1px solid #e5e7eb",borderRadius:6,fontSize:12,minWidth:220}}>
                          <option value="">{c.obbligatorio?"— Scegli —":"— Lascia vuoto, sistemo dopo —"}</option>
                          {c.options.map(o=><option key={o} value={o}>{o}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
                <div style={{display:"flex",gap:8}}>
                  <button onClick={()=>setPasso(2)} style={{padding:"7px 14px",border:"1px solid #e5e7eb",borderRadius:7,background:"#fff",fontSize:12,cursor:"pointer"}}>← Indietro</button>
                  <button onClick={conferma} disabled={importando} className="btn btn-primary" style={{padding:"7px 14px",fontSize:12}}>
                    {importando?"Importazione...":"✅ Conferma importazione"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
